import React, { createContext, useState, useEffect } from 'react';

export const CurrencyContext = createContext();

export function CurrencyProvider({ children }) {
  const [currency, setCurrency] = useState(() => {
    // Default to MYR
    return localStorage.getItem('currency') || 'MYR';
  });

  const [exchangeRates, setExchangeRates] = useState({
    MYR: 1,
    USD: 0.22,
    EUR: 0.20,
    GBP: 0.17,
    SGD: 0.30,
    THB: 7.50,
    IDR: 3500,
  });

  const [loading, setLoading] = useState(false);

  // Fetch exchange rates from free API on mount
  useEffect(() => {
    const fetchRates = async () => {
      setLoading(true);
      try {
        // Using exchangerate-api.com free tier (no key required for basic calls)
        const response = await fetch('https://api.exchangerate-api.com/v4/latest/MYR');
        const data = await response.json();

        if (data.rates) {
          setExchangeRates({
            MYR: 1,
            USD: data.rates.USD || 0.22,
            EUR: data.rates.EUR || 0.20,
            GBP: data.rates.GBP || 0.17,
            SGD: data.rates.SGD || 0.30,
            THB: data.rates.THB || 7.50,
            IDR: data.rates.IDR || 3500,
          });
        }
      } catch (error) {
        console.error('Error fetching exchange rates:', error);
        // Fallback rates if API fails
      } finally {
        setLoading(false);
      }
    };

    fetchRates();
    // Refresh rates every 6 hours
    const interval = setInterval(fetchRates, 6 * 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const changeCurrency = (newCurrency) => {
    setCurrency(newCurrency);
    localStorage.setItem('currency', newCurrency);
  };

  const convertPrice = (priceInMYR, targetCurrency = currency) => {
    if (!exchangeRates[targetCurrency]) return priceInMYR;
    return (priceInMYR * exchangeRates[targetCurrency]).toFixed(2);
  };

  const getCurrencySymbol = (curr = currency) => {
    const symbols = {
      MYR: 'RM',
      USD: '$',
      EUR: '€',
      GBP: '£',
      SGD: 'S$',
      THB: '฿',
      IDR: 'Rp',
    };
    return symbols[curr] || curr;
  };

  const value = {
    currency,
    changeCurrency,
    convertPrice,
    getCurrencySymbol,
    exchangeRates,
    loading,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = React.useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within CurrencyProvider');
  }
  return context;
}
